import { ConnectProto } from "@racoon/api";
import { calculateTotalRouteDuration, parseISO8601Duration } from "~/shared/lib/formatters";
import { Namespace, TranslationKey } from "@racoon/i18n";

/**
 * Driving time of the calculated legs plus the service time of every stop, in
 * whole minutes. `calculateTotalRouteTimeWithService` renders the same figure
 * for the operator; this is the value callers need when they have to put an
 * actual timestamp on the route (a pseudo-route's `plan_end_at`).
 *
 * A pickup location without an explicit `service_time` falls back to 30 min,
 * the same default the backend applies.
 *
 * `jobsData` has to be addressable by the ids in `selectedJobs`; a stop that
 * cannot be looked up adds no service time.
 */
export const calculateRouteMinutesWithService = (
  routeLegs: { steps?: { duration?: string }[] }[] | undefined,
  selectedJobs: number[],
  jobsData: any[],
): number => {
  if (!routeLegs || routeLegs.length === 0) return 0;

  const drivingMinutes = routeLegs.reduce<number>(
    (total, leg) =>
      total +
      (leg.steps ?? []).reduce<number>(
        (stepTotal, step) => stepTotal + parseISO8601Duration(step.duration || "PT0S"),
        0,
      ),
    0,
  );

  const serviceSeconds = selectedJobs.reduce<number>((total, jobId) => {
    const job = jobsData.find((candidate) => Number(candidate.id) === jobId);
    const pickupLocation = job?.pickupLocation || job?.pickup_location;
    if (!pickupLocation) return total;
    const serviceTime = pickupLocation.serviceTime ?? pickupLocation.service_time ?? 1800;
    return total + Number(serviceTime);
  }, 0);

  return drivingMinutes + Math.round(serviceSeconds / 60);
};

export const calculateTotalRouteTimeWithService = (
  routeLegs: any[],
  selectedJobs: number[],
  jobsData: any[],
  startTime?: Date | string,
  t?: (key: TranslationKey<Namespace.Routes>) => string,
) => {
  if (!routeLegs || routeLegs.length === 0) return "--";

  const baseDuration = calculateTotalRouteDuration(routeLegs);

  let totalServiceTime = 0;
  selectedJobs.forEach((jobId) => {
    const job = jobsData.find((j) => Number(j.id) === jobId);
    const pickupLocation = job?.pickupLocation || job?.pickup_location;
    if (pickupLocation) {
      const serviceTime = pickupLocation.serviceTime ?? pickupLocation.service_time ?? 1800;
      totalServiceTime += Number(serviceTime);
    }
  });

  const serviceMinutes = Math.round(totalServiceTime / 60);

  const baseMinutes = (() => {
    const hourMatch = baseDuration.match(/(\d+)\s*ч/);
    const minuteMatch = baseDuration.match(/(\d+)\s*мин/);

    const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;

    return hours * 60 + minutes;
  })();

  const totalMinutes = baseMinutes + serviceMinutes;
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  // Format start and end times as HH:MM
  const timeRangeString = startTime
    ? (() => {
        const startDate = typeof startTime === "string" ? new Date(startTime) : startTime;
        if (isNaN(startDate.getTime())) return "";
        const endDate = new Date(startDate.getTime() + totalMinutes * 60 * 1000);

        const startStr = `${startDate.getHours().toString().padStart(2, "0")}:${startDate.getMinutes().toString().padStart(2, "0")}`;
        const endStr = `${endDate.getHours().toString().padStart(2, "0")}:${endDate.getMinutes().toString().padStart(2, "0")}`;
        return `(${startStr} - ${endStr})`;
      })()
    : "";

  // Get translations for time units
  const hourWord = t ? getHourWord(totalHours, t) : "час";
  const minuteWord = t ? getMinuteWord(remainingMinutes, t) : "мин";

  if (totalHours > 0) {
    const result = `${totalHours} ${hourWord} ${remainingMinutes} ${minuteWord}`;
    return timeRangeString ? `${result} ${timeRangeString}` : result;
  }

  const result = `${remainingMinutes} ${minuteWord}`;
  return timeRangeString ? `${result} ${timeRangeString}` : result;
};

// Function for correct pluralization of the word "hour"
const getHourWord = (
  hours: number,
  t: (key: TranslationKey<Namespace.Routes>) => string,
): string => {
  const lastDigit = hours % 10;
  const lastTwoDigits = hours % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return t("hoursMany" as any);
  }

  if (lastDigit === 1) {
    return t("hour" as any);
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return t("hours" as any);
  }

  return t("hoursMany" as any);
};

const getMinuteWord = (
  minutes: number,
  t: (key: TranslationKey<Namespace.Routes>) => string,
): string => {
  const lastDigit = minutes % 10;
  const lastTwoDigits = minutes % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return t("minutesMany" as any);
  }

  if (lastDigit === 1) {
    return t("minute" as any);
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return t("minutes" as any);
  }

  return t("minutesMany" as any);
};

export const isTruckFree = (truck: ConnectProto.CurrentTruckStatus) => {
  const isShiftIdle = truck.currentShift?.status === ConnectProto.DriverOnShiftStatus.TRUCK_IDLE;

  const hasActiveRoute =
    truck.currentRoute &&
    truck.currentRoute.status &&
    truck.currentRoute.status !== ConnectProto.RouteStatus.COMPLETED &&
    truck.currentRoute.status !== ConnectProto.RouteStatus.CANCELLED;

  const hasActiveJobs =
    truck.currentRoute?.jobs?.some(
      (job) =>
        job.status === ConnectProto.JobStatus.ASSIGNED ||
        job.status === ConnectProto.JobStatus.ENROUTE ||
        job.status === ConnectProto.JobStatus.ON_SERVICE,
    ) || false;

  return isShiftIdle && !hasActiveRoute && !hasActiveJobs;
};

export const filterTrucks = (
  trucks: ConnectProto.CurrentTruckStatus[],
  query: string,
  predicate: (truck: ConnectProto.CurrentTruckStatus) => boolean,
) => {
  const searchQuery = query.toLowerCase().trim();
  const filteredList = searchQuery
    ? trucks.filter((truck) => {
        const plateNumber = truck.truck?.plateNumber?.toLowerCase() || "";
        const driverName = truck.currentShift?.driver
          ? `${truck.currentShift.driver.firstName || ""} ${truck.currentShift.driver.lastName || ""}`.toLowerCase()
          : "";

        return plateNumber.includes(searchQuery) || driverName.includes(searchQuery);
      })
    : trucks;

  const trucksWithFlags = filteredList.map((truck) => ({
    truck,
    isFree: predicate(truck),
    plateNumber: truck.truck?.plateNumber || "",
  }));

  trucksWithFlags.sort((a, b) => {
    const aHasShift = !!a.truck.currentShift;
    const bHasShift = !!b.truck.currentShift;

    if (aHasShift && !bHasShift) return -1;
    if (!aHasShift && bHasShift) return 1;

    if (a.isFree && !b.isFree) return -1;
    if (!a.isFree && b.isFree) return 1;

    return a.plateNumber.localeCompare(b.plateNumber);
  });

  return trucksWithFlags.map(({ truck }) => truck);
};
